import { Post } from "@/core/types/postTypes";
import { MediaItemData } from "@/core/MediaItem/MediaItem.model";
import { createPortalClient, PortalRole } from "@/services/portalClient";
import { logger } from "@/utils/logger";

export interface ClientData {
  clientName: string;
  posts: Post[];
  pendingMediaCount?: number;
}

export interface PostUpdateRequest {
  caption?: string;
  status?: string;
}

class ClientService {
  // Logout (JWT is stateless, just clear client-side token)
  async logout(clientId: string): Promise<{ success: boolean }> {
    return { success: true };
  }

  // Get client data using JWT token
  async getClientData(
    clientId: string,
    role: PortalRole = 'client',
  ): Promise<ClientData> {
    const portalClient = createPortalClient(clientId, role);
    return portalClient.get(`/portal/${role}/${clientId}`);
  }

  // Update post using JWT token
  async updatePost(
    clientId: string,
    postId: string,
    updates: PostUpdateRequest,
    role: PortalRole = 'client',
  ): Promise<void> {
    const portalClient = createPortalClient(clientId, role);
    await portalClient.patch(`/portal/${role}/${clientId}/posts/${postId}`, updates);
  }

  /**
   * Stamp the post as reviewed by the client. Status-neutral — does
   * not transition the post lifecycle. Driven by the "Approva" button
   * on the post review page.
   */
  async reviewPost(
    clientId: string,
    postId: string,
    role: PortalRole = 'client',
  ): Promise<void> {
    const portalClient = createPortalClient(clientId, role);
    await portalClient.post(`/portal/${role}/${clientId}/posts/${postId}/review`, {});
  }

  /** Clear the client-review stamp (toggle Approva off). */
  async unreviewPost(
    clientId: string,
    postId: string,
    role: PortalRole = 'client',
  ): Promise<void> {
    const portalClient = createPortalClient(clientId, role);
    await portalClient.delete(`/portal/${role}/${clientId}/posts/${postId}/review`);
  }

  // Media methods with backend filtering support
  async getPendingMedia(
    clientId: string,
    stage?: string,
    role: PortalRole = 'client',
    contentType?: 'fotos' | 'reels' | 'stories',
    page: number = 1,
    perPage: number = 50,
    rated?: boolean,
  ): Promise<{
    mediaItems: MediaItemData[];
    totalCount: number;
    page: number;
    perPage: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  }> {
    const portalClient = createPortalClient(clientId, role);

    // Build query params
    const params = new URLSearchParams();
    if (stage) {
      params.set('stage', stage);
    }
    if (contentType) {
      params.set('content_type', contentType);
    }
    if (rated !== undefined) {
      params.set('rated', String(rated));
    }
    params.set('page', page.toString());
    params.set('per_page', perPage.toString());

    const queryString = params.toString();
    const url = queryString
      ? `/portal/${role}/${clientId}/media?${queryString}`
      : `/portal/${role}/${clientId}/media`;

    const data = await portalClient.get<{
      mediaItems: MediaItemData[];
      totalCount: number;
      page: number;
      perPage: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    }>(url);

    return {
      mediaItems: data.mediaItems || [],
      totalCount: data.totalCount || 0,
      page: data.page || 1,
      perPage: data.perPage || 50,
      totalPages: data.totalPages || 1,
      hasNext: data.hasNext || false,
      hasPrev: data.hasPrev || false,
    };
  }

  async rateMedia(
    clientId: string,
    mediaId: number,
    rating?: number,
    comment?: string,
    role: PortalRole = 'client',
  ): Promise<void> {
    logger.info("Rating media:", mediaId, "with rating:", rating);

    const portalClient = createPortalClient(clientId, role);
    const requestBody: Record<string, unknown> = {};
    if (rating != null) {
      requestBody.rating = rating;
    }
    if (comment != null) {
      requestBody.comment = comment;
    }

    await portalClient.post(`/portal/${role}/${clientId}/media/${mediaId}/rate`, requestBody);
    logger.info("Media rate API call successful");
  }

  async updateMediaLabel(
    clientId: string,
    mediaId: number,
    label: string,
    role: PortalRole = 'client',
  ): Promise<void> {
    logger.info("Updating media label:", mediaId, label);

    const portalClient = createPortalClient(clientId, role);
    await portalClient.patch(`/portal/${role}/${clientId}/media/${mediaId}`, { label });

    logger.info("Media label update API call successful");
  }

  async addNote(
    clientId: string,
    postId: string,
    content: string,
  ): Promise<{ id: string; content: string; createdAt: string }> {
    const portalClient = createPortalClient(clientId, 'client');
    const response = await portalClient.post<{
      success: boolean;
      note: { id: string; content: string; createdAt: string };
    }>(`/portal/client/${clientId}/posts/${postId}/notes`, { content });
    return response.note;
  }

  async getNotes(
    clientId: string,
    postId: string,
  ): Promise<Array<{ id: string; content: string; createdAt: string }>> {
    const portalClient = createPortalClient(clientId, 'client');
    const response = await portalClient.get<{
      success: boolean;
      notes: Array<{ id: string; content: string; createdAt: string }>;
    }>(`/portal/client/${clientId}/posts/${postId}/notes`);
    return response.notes;
  }

  async updateNote(
    clientId: string,
    postId: string,
    noteId: string,
    content: string,
  ): Promise<{ id: string; content: string; createdAt: string }> {
    const portalClient = createPortalClient(clientId, 'client');
    const response = await portalClient.patch<{
      success: boolean;
      note: { id: string; content: string; createdAt: string };
    }>(`/portal/client/${clientId}/posts/${postId}/notes/${noteId}`, { content });
    return response.note;
  }

  async deleteNote(
    clientId: string,
    postId: string,
    noteId: string,
  ): Promise<void> {
    const portalClient = createPortalClient(clientId, 'client');
    await portalClient.delete(
      `/portal/client/${clientId}/posts/${postId}/notes/${noteId}`,
    );
  }
}

export const clientService = new ClientService();
